import React, { useRef, useState } from "react";
import { Fade } from "reactstrap";

import { useOutsideClickDetection } from "../../../../../lib/hooks/useOutsideClickDetection";
import { useConfirmation } from "../../../../../lib/context/Confirmation/ConfirmationContext";

import { Button } from "../../../Button/Button";
import { DebugDeviceInfo } from "../DebugDeviceInfo";
import { SectionHeader } from "../ui/SectionHeader";
import { Notice } from "../ui/Notice";

interface Props {
  id: string;
  deviceId: string;
  deviceName: string;
  workspaceId?: string;
  // Only devices that support remote restart show the button
  showRestart: boolean;
  // Without push, Restart is disabled. Debug always stays available
  pushAvailable: boolean;
  onRestart: () => void;
}

export const DeviceActions = ({
  id,
  deviceId,
  deviceName,
  workspaceId,
  showRestart,
  pushAvailable,
  onRestart,
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [isDebugInstant, setIsDebugInstant] = useState(false);
  const { confirm } = useConfirmation();

  const closeDebug = (instant = false) => {
    const popover = ref.current?.querySelector(".DeviceActions__popover");
    if (popover?.contains(document.activeElement)) {
      ref.current?.querySelector<HTMLButtonElement>(".Button")?.focus();
    }
    setIsDebugInstant(instant);
    setIsDebugOpen(false);
  };

  useOutsideClickDetection(
    ref,
    () => {
      if (isDebugOpen) closeDebug();
    },
    isDebugOpen
  );

  const toggleDebug = (event: React.MouseEvent) => {
    const instant = event.detail === 0;
    if (isDebugOpen) {
      closeDebug(instant);
    } else {
      setIsDebugInstant(instant);
      setIsDebugOpen(true);
    }
  };

  const handleRestart = async () => {
    try {
      await confirm({
        title: `Restart ${deviceName}?`,
        description:
          "The display will be briefly unavailable. Settings and bookings will be kept.",
      });
    } catch {
      // Cancelled
      return;
    }

    onRestart();
  };

  return (
    <section className="DeviceSection DeviceActions">
      <SectionHeader title="Device actions" />

      <div className="DeviceActions__buttons">
        {showRestart && (
          <Button
            type="button"
            title="Restart device"
            className="Button--outlined"
            disabled={!pushAvailable}
            onClick={handleRestart}
          />
        )}
        <div
          className="DeviceActions__debug"
          ref={ref}
          onKeyDown={(event) => {
            if (event.key === "Escape" && isDebugOpen) {
              event.preventDefault();
              event.stopPropagation();
              closeDebug(true);
            }
          }}
        >
          <Button
            type="button"
            title="Debug device"
            className="Button--outlined Button--muted"
            aria-expanded={isDebugOpen}
            onClick={toggleDebug}
          />
          <Fade
            in={isDebugOpen}
            mountOnEnter
            unmountOnExit
            enter={!isDebugInstant}
            exit={!isDebugInstant}
            timeout={{ enter: 160, exit: 125 }}
            baseClass=""
            baseClassActive=""
            className="DeviceActions__popover"
            data-open={isDebugOpen}
            data-instant={isDebugInstant}
            aria-hidden={!isDebugOpen}
            {...(!isDebugOpen ? { inert: "" } : {})}
          >
            <DebugDeviceInfo
              id={id}
              deviceId={deviceId}
              workspaceId={workspaceId}
            />
          </Fade>
        </div>
      </div>

      {showRestart && (
        <div className="DeviceSection__meta">
          <p>
            Restart briefly interrupts the display. Settings and bookings are
            kept.
          </p>
        </div>
      )}

      {!pushAvailable && (
        <>
          <Notice tone="danger">Push connectivity unavailable</Notice>
          <div className="DeviceSection__meta">
            <p>
              Remote actions become available when push connectivity is
              restored.
            </p>
          </div>
        </>
      )}
    </section>
  );
};
